import { ReactElement, useMemo } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';

// Components
import { GroupSection } from './GroupSection';

// Constants
import { SCENE_7_8_EXIT_DURATION } from '@/constants';
import { CATEGORY_TRANSITION_GAP } from './constants';

// Types
import { Group, TeamMember } from '../../types';

// Utils
import { calculateCategoryDurationFromCount } from '../../utils';

interface MainSectionProps {
  animationDelay?: number; // Delay before animations start
  exitStartFrame?: number; // Scene-local frame when unified exit starts
  exitDuration?: number; // Exit animation duration in frames
  supporters: TeamMember[];
  mentees: TeamMember[];
  supportees: TeamMember[];
}

export const MainSection = ({
  animationDelay = 0,
  exitStartFrame = 0,
  exitDuration = SCENE_7_8_EXIT_DURATION,
  supporters = [],
  mentees = [],
  supportees = [],
}: MainSectionProps): ReactElement => {
  const frame = useCurrentFrame();
  const { width, fps } = useVideoConfig();

  const groups: Group[] = useMemo(
    () => [
      { type: 'supporters', label: 'SUPPORTERS', members: supporters },
      { type: 'supportees', label: 'SUPPORTEES', members: supportees },
      { type: 'mentees', label: 'MENTEES', members: mentees },
    ],
    [supporters, mentees, supportees],
  );

  // Only include groups with members
  const activeGroups = useMemo(
    () => groups.filter((group) => group.members.length),
    [groups],
  );

  // Calculate when each group starts (sequential with transition gaps)
  const groupStartFrames = useMemo(() => {
    const startFrames: number[] = [];
    let currentStart = animationDelay;

    activeGroups.forEach((group, index) => {
      startFrames.push(currentStart);

      if (index < activeGroups.length - 1) {
        currentStart +=
          calculateCategoryDurationFromCount(group.members.length) +
          CATEGORY_TRANSITION_GAP;
      }
    });

    return startFrames;
  }, [animationDelay, activeGroups]);

  // Find which group should be visible at current frame
  const activeGroupIndex = useMemo(() => {
    // Loop backward to find the most recent group that has started
    for (let i = activeGroups.length - 1; i >= 0; i--) {
      if (frame >= (groupStartFrames[i] ?? 0)) return i;
    }

    return -1;
  }, [frame, activeGroups.length, groupStartFrames]);

  return (
    <AbsoluteFill>
      <div className="relative" style={{ width, height: '100%' }}>
        {activeGroups.map((group, index) => {
          const startFrame = groupStartFrames[index] ?? 0;
          const isLastGroup = index === activeGroups.length - 1;
          const isActiveGroup = index === activeGroupIndex;

          return (
            <GroupSection
              key={group.type}
              group={group}
              groupStartFrame={startFrame}
              containerWidth={width}
              fps={fps}
              currentFrame={frame}
              isLastGroup={isLastGroup}
              isActiveGroup={isActiveGroup}
              sceneExitStartFrame={exitStartFrame}
              sceneExitDuration={exitDuration}
            />
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
